import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaArrowLeft, FaPlus, FaSearch, FaEdit, FaTrash, FaHistory, FaTimes, FaCheckCircle, FaPrint } from 'react-icons/fa';

const API = 'https://api.alquilacarbolivia.com/api';

const ESTILO_TIPO = {
    compra: { bg: '#d1fae5', color: '#059669', label: 'COMPRA' },
    entrega: { bg: '#fef3c7', color: '#92400e', label: 'ENTREGA' },
    ajuste: { bg: '#dbeafe', color: '#1d4ed8', label: 'AJUSTE' }
};

const fmtNum = (n) => { const v = parseFloat(n); return isNaN(v) ? '0' : String(v); };
const fmtFecha = (f) => new Date(f).toLocaleString('es-BO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function Insumos({ sucursalId, user, goBack }) {
    const [insumos, setInsumos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const [filtroStock, setFiltroStock] = useState('todos'); // todos | ok | bajo | sin
    const [duracion, setDuracion] = useState({});
    const [tab, setTab] = useState('insumos');

    // Modal crear / editar
    const [showForm, setShowForm] = useState(false);
    const [editando, setEditando] = useState(null);
    const [form, setForm] = useState({ nombre: '', unidad_medida: '', stock_inicial: '' });

    // Modal ajuste de stock
    const [ajusteInsumo, setAjusteInsumo] = useState(null);
    const [ajuste, setAjuste] = useState({ nueva_cantidad: '', motivo: '', pin: '' });

    // Modal kardex
    const [kardexInsumo, setKardexInsumo] = useState(null);
    const [movimientos, setMovimientos] = useState([]);

    // Entregas a local
    const [entregas, setEntregas] = useState([]);
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroDesde, setFiltroDesde] = useState('');
    const [filtroHasta, setFiltroHasta] = useState('');
    const [showEntrega, setShowEntrega] = useState(false);
    const [entregaEditando, setEntregaEditando] = useState(null);
    const [entregaForm, setEntregaForm] = useState({ entregado_por: '', recibido_por: '', observaciones: '', pin: '' });
    const [itemsEntrega, setItemsEntrega] = useState([]);
    const [busquedaEntrega, setBusquedaEntrega] = useState('');
    const [guardandoEntrega, setGuardandoEntrega] = useState(false);

    useEffect(() => { cargar(); }, [sucursalId]);

    useEffect(() => {
        if (tab === 'entregas') cargarEntregas();
    }, [tab, sucursalId, filtroEstado, filtroDesde, filtroHasta]);

    const cargar = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API}/insumos/${sucursalId}`);
            setInsumos(res.data);
            try {
                const d = await axios.get(`${API}/insumos/duracion/${sucursalId}`);
                const mapa = {};
                d.data.forEach(r => { mapa[r.id] = r; });
                setDuracion(mapa);
            } catch (e) { console.error(e); }
        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'No se pudieron cargar los insumos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const abrirNuevo = () => {
        setEditando(null);
        setForm({ nombre: '', unidad_medida: '', stock_inicial: '' });
        setShowForm(true);
    };

    const abrirEditar = (insumo) => {
        setEditando(insumo);
        setForm({ nombre: insumo.nombre, unidad_medida: insumo.unidad_medida || '', stock_inicial: '' });
        setShowForm(true);
    };

    const guardarInsumo = async (e) => {
        e.preventDefault();
        if (!form.nombre.trim()) {
            Swal.fire('Atención', 'Escribe el nombre del insumo', 'warning');
            return;
        }
        const eraEdicion = !!editando;
        try {
            if (eraEdicion) {
                await axios.put(`${API}/insumos/${editando.id}`, { nombre: form.nombre, unidad_medida: form.unidad_medida });
            } else {
                await axios.post(`${API}/insumos`, {
                    sucursal_id: sucursalId,
                    nombre: form.nombre,
                    unidad_medida: form.unidad_medida,
                    stock_inicial: form.stock_inicial,
                    usuario_id: user?.id
                });
            }
            setShowForm(false);
            Swal.fire({ icon: 'success', title: eraEdicion ? 'Insumo actualizado' : 'Insumo creado', timer: 1500, showConfirmButton: false });
            cargar();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.error || 'No se pudo guardar el insumo', 'error');
        }
    };

    const abrirAjuste = (insumo) => {
        setAjusteInsumo(insumo);
        setAjuste({ nueva_cantidad: fmtNum(insumo.stock_almacen), motivo: '', pin: '' });
    };

    const guardarAjuste = async () => {
        if (ajuste.nueva_cantidad === '' || isNaN(parseFloat(ajuste.nueva_cantidad)) || parseFloat(ajuste.nueva_cantidad) < 0) {
            Swal.fire('Atención', 'Escribe una cantidad válida (0 o más)', 'warning');
            return;
        }
        if (!ajuste.motivo.trim()) {
            Swal.fire('Atención', 'Escribe el motivo del ajuste', 'warning');
            return;
        }
        if (!ajuste.pin) {
            Swal.fire('Atención', 'Ingresa el PIN de seguridad', 'warning');
            return;
        }
        try {
            await axios.post(`${API}/insumos/ajuste`, {
                insumo_id: ajusteInsumo.id,
                nueva_cantidad: ajuste.nueva_cantidad,
                motivo: ajuste.motivo,
                pin: ajuste.pin,
                usuario_id: user?.id
            });
            setAjusteInsumo(null);
            Swal.fire({ icon: 'success', title: 'Stock actualizado', timer: 1500, showConfirmButton: false });
            cargar();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.error || 'No se pudo ajustar el stock', 'error');
        }
    };

    const eliminarInsumo = async (insumo) => {
        const { value: pin } = await Swal.fire({
            title: `Eliminar "${insumo.nombre}"`,
            text: 'Si ya tiene movimientos, se desactiva y conserva su historial. Solo se puede con stock en 0.',
            input: 'password',
            inputLabel: 'PIN de seguridad',
            inputAttributes: { autocomplete: 'new-password' },
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            confirmButtonColor: '#dc3545',
            cancelButtonText: 'Cancelar'
        });
        if (!pin) return;
        try {
            const res = await axios.delete(`${API}/insumos/${insumo.id}`, { data: { pin } });
            Swal.fire({ icon: 'success', title: res.data.message || 'Listo', timer: 1800, showConfirmButton: false });
            cargar();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.error || 'No se pudo eliminar el insumo', 'error');
        }
    };

    const verKardex = async (insumo) => {
        try {
            const res = await axios.get(`${API}/insumos/movimientos/${insumo.id}`);
            setMovimientos(res.data);
            setKardexInsumo(insumo);
        } catch (error) {
            Swal.fire('Error', 'No se pudo cargar el kardex', 'error');
        }
    };

    // ---------- ENTREGAS A LOCAL ----------
    const cargarEntregas = async () => {
        try {
            const params = {};
            if (filtroEstado) params.estado = filtroEstado;
            if (filtroDesde) params.desde = filtroDesde;
            if (filtroHasta) params.hasta = filtroHasta;
            const res = await axios.get(`${API}/entregas-local/${sucursalId}`, { params });
            setEntregas(res.data);
        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'No se pudo cargar el historial de entregas', 'error');
        }
    };

    const abrirPDF = (id) => {
        window.open(`${API}/entregas-local/pdf/${id}`, '_blank');
    };

    const abrirNuevaEntrega = () => {
        setEntregaEditando(null);
        setEntregaForm({ entregado_por: user?.nombre || '', recibido_por: '', observaciones: '', pin: '' });
        setItemsEntrega([]);
        setBusquedaEntrega('');
        setShowEntrega(true);
    };

    const abrirEditarEntrega = (ent) => {
        setEntregaEditando(ent);
        setEntregaForm({
            entregado_por: ent.entregado_por || '',
            recibido_por: ent.recibido_por || '',
            observaciones: ent.observaciones || '',
            pin: ''
        });
        // Al editar, lo disponible es el stock actual mas lo que esta entrega ya llevaba
        setItemsEntrega((ent.items || []).map(it => {
            const ins = insumos.find(x => x.id === it.insumo_id);
            return {
                insumo_id: it.insumo_id,
                nombre: it.nombre,
                unidad_medida: it.unidad_medida,
                cantidad: String(parseFloat(it.cantidad)),
                disponible: (ins ? parseFloat(ins.stock_almacen) : 0) + parseFloat(it.cantidad)
            };
        }));
        setBusquedaEntrega('');
        setShowEntrega(true);
    };

    const agregarItemEntrega = (ins) => {
        if (itemsEntrega.find(it => it.insumo_id === ins.id)) return;
        setItemsEntrega([...itemsEntrega, {
            insumo_id: ins.id,
            nombre: ins.nombre,
            unidad_medida: ins.unidad_medida,
            cantidad: '',
            disponible: parseFloat(ins.stock_almacen)
        }]);
        setBusquedaEntrega('');
    };

    const cambiarCantidadItem = (insumo_id, valor) => {
        setItemsEntrega(itemsEntrega.map(it => it.insumo_id === insumo_id ? { ...it, cantidad: valor } : it));
    };

    const quitarItemEntrega = (insumo_id) => {
        setItemsEntrega(itemsEntrega.filter(it => it.insumo_id !== insumo_id));
    };

    const guardarEntrega = async () => {
        if (guardandoEntrega) return;
        if (itemsEntrega.length === 0) {
            Swal.fire('Atención', 'Agrega al menos un insumo', 'warning');
            return;
        }
        for (const it of itemsEntrega) {
            const c = parseFloat(it.cantidad);
            if (!(c > 0)) {
                Swal.fire('Atención', `Escribe la cantidad de ${it.nombre}`, 'warning');
                return;
            }
            if (c > it.disponible) {
                Swal.fire('Stock insuficiente', `De ${it.nombre} solo hay ${fmtNum(it.disponible)} en almacén`, 'warning');
                return;
            }
        }
        if (entregaEditando && !entregaForm.pin) {
            Swal.fire('Atención', 'Ingresa el PIN de seguridad', 'warning');
            return;
        }
        const payload = {
            usuario_id: user?.id,
            entregado_por: entregaForm.entregado_por,
            recibido_por: entregaForm.recibido_por,
            observaciones: entregaForm.observaciones,
            items: itemsEntrega.map(it => ({ insumo_id: it.insumo_id, cantidad: it.cantidad }))
        };
        const eraEdicion = !!entregaEditando;
        setGuardandoEntrega(true);
        try {
            let res;
            if (eraEdicion) {
                res = await axios.put(`${API}/entregas-local/${entregaEditando.id}`, { ...payload, pin: entregaForm.pin });
            } else {
                res = await axios.post(`${API}/entregas-local`, { ...payload, sucursal_id: sucursalId });
            }
            setShowEntrega(false);
            cargar();
            cargarEntregas();
            const r = await Swal.fire({
                icon: 'success',
                title: `Entrega ${res.data.numero} ${eraEdicion ? 'actualizada' : 'registrada'}`,
                showCancelButton: true,
                confirmButtonText: 'Imprimir comprobante',
                cancelButtonText: 'Cerrar'
            });
            if (r.isConfirmed) abrirPDF(res.data.id);
        } catch (error) {
            Swal.fire('Error', error.response?.data?.error || 'No se pudo guardar la entrega', 'error');
        } finally {
            setGuardandoEntrega(false);
        }
    };

    const anularEntrega = async (ent) => {
        const { value } = await Swal.fire({
            title: `Anular ${ent.numero}`,
            html: '<textarea id="anular-motivo" class="swal2-textarea" placeholder="Motivo (obligatorio)"></textarea>' +
                  '<input id="anular-pin" type="password" autocomplete="new-password" class="swal2-input" placeholder="PIN de seguridad">',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Anular entrega',
            confirmButtonColor: '#dc3545',
            cancelButtonText: 'Cancelar',
            preConfirm: () => {
                const motivo = document.getElementById('anular-motivo').value.trim();
                const pin = document.getElementById('anular-pin').value;
                if (!motivo || !pin) {
                    Swal.showValidationMessage('Escribe el motivo y el PIN');
                    return false;
                }
                return { motivo, pin };
            }
        });
        if (!value) return;
        try {
            await axios.post(`${API}/entregas-local/${ent.id}/anular`, { ...value, usuario_id: user?.id });
            Swal.fire({ icon: 'success', title: 'Entrega anulada y stock devuelto al almacén', timer: 2200, showConfirmButton: false });
            cargar();
            cargarEntregas();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.error || 'No se pudo anular la entrega', 'error');
        }
    };

    const diasDe = (id) => {
        const d = duracion[id];
        return d && d.dias_restantes_estimado !== null && d.dias_restantes_estimado !== undefined ? d.dias_restantes_estimado : null;
    };
    const estadoDe = (i) => {
        const stock = parseFloat(i.stock_almacen);
        const dias = diasDe(i.id);
        if (stock <= 0) return { clave: 'sin', color: '#ef4444', bg: '#fee2e2', texto: 'Sin stock' };
        if (dias !== null && dias <= 7) return { clave: 'bajo', color: '#d97706', bg: '#fef3c7', texto: 'Por acabarse' };
        return { clave: 'ok', color: '#059669', bg: '#d1fae5', texto: 'Disponible' };
    };
    const porAcabarse = insumos.filter(i => estadoDe(i).clave === 'bajo').length;
    const filtrados = insumos
        .filter(i => i.nombre.toLowerCase().includes(busqueda.trim().toLowerCase()))
        .filter(i => filtroStock === 'todos' || estadoDe(i).clave === filtroStock);

    const entregarDirecto = (insumo) => {
        setTab('entregas');
        setEntregaEditando(null);
        setEntregaForm({ entregado_por: user?.nombre || '', recibido_por: '', observaciones: '', pin: '' });
        setItemsEntrega([{
            insumo_id: insumo.id,
            nombre: insumo.nombre,
            unidad_medida: insumo.unidad_medida,
            cantidad: '',
            disponible: parseFloat(insumo.stock_almacen)
        }]);
        setBusquedaEntrega('');
        setShowEntrega(true);
    };
    const sinStock = insumos.filter(i => parseFloat(i.stock_almacen) <= 0).length;
    const hoyISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/La_Paz' });
    const haceDiasISO = (n) => {
        const d = new Date();
        d.setDate(d.getDate() - n);
        return d.toLocaleDateString('en-CA', { timeZone: 'America/La_Paz' });
    };
    const inicioMesISO = () => hoyISO().slice(0, 8) + '01';
    const entActivas = entregas.filter(e => e.estado === 'activa');
    const entAnuladas = entregas.length - entActivas.length;
    const insumosDistintos = new Set(entActivas.flatMap(e => (e.items || []).map(it => it.insumo_id))).size;
    const nuevaNum = parseFloat(ajuste.nueva_cantidad);
    const diffAjuste = ajusteInsumo && !isNaN(nuevaNum)
        ? Math.round((nuevaNum - parseFloat(ajusteInsumo.stock_almacen)) * 100) / 100
        : null;

    return (
        <div className="min-vh-100" style={{ background: '#f8fafc' }}>
            {/* ENCABEZADO */}
            <div style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)', padding: '1.5rem' }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3">
                        <button className="btn btn-light fw-bold" onClick={goBack}>
                            <FaArrowLeft className="me-2" /> Volver
                        </button>
                        <div>
                            <h4 className="m-0 text-white fw-bold">📦 Insumos</h4>
                            <small className="text-white" style={{ opacity: 0.8 }}>
                                {insumos.length} insumos • {sinStock} sin stock
                            </small>
                        </div>
                    </div>
                    {tab === 'entregas' && (
                        <button className="btn btn-light fw-bold" onClick={abrirNuevaEntrega}>
                            <FaPlus className="me-2" /> Nueva Entrega
                        </button>
                    )}
                    {tab === 'insumos' && (
                        <button className="btn btn-light fw-bold" onClick={abrirNuevo}>
                            <FaPlus className="me-2" /> Nuevo Insumo
                        </button>
                    )}
                </div>
            </div>

            {/* PESTAÑAS */}
            <div className="px-4 pt-3 d-flex gap-2 flex-wrap">
                <button className={`btn fw-bold ${tab === 'insumos' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTab('insumos')}>
                    Insumos
                </button>
                <button className={`btn fw-bold ${tab === 'entregas' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setTab('entregas')}>
                    Entregas a local
                </button>
                {/* TABS_EXTRA */}
            </div>

            {/* CONTENIDO: INSUMOS */}
            {tab === 'insumos' && (
                <div className="p-4">
                    {/* Resumen: tocar una tarjeta filtra la lista */}
                    <div className="row g-3 mb-4">
                        {[
                            { titulo: 'Total de insumos', valor: insumos.length, icono: '📦', grad: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', filtro: 'todos' },
                            { titulo: 'Disponibles', valor: insumos.length - sinStock - porAcabarse, icono: '✅', grad: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', filtro: 'ok' },
                            { titulo: 'Por acabarse', valor: porAcabarse, icono: '⏳', grad: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', filtro: 'bajo' },
                            { titulo: 'Sin stock', valor: sinStock, icono: '⚠️', grad: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', filtro: 'sin' }
                        ].map(c => (
                            <div key={c.titulo} className="col-6 col-lg-3">
                                <div
                                    className="text-white p-3 shadow-sm"
                                    style={{ background: c.grad, borderRadius: '16px', cursor: 'pointer', outline: filtroStock === c.filtro ? '3px solid rgba(15,23,42,0.35)' : 'none' }}
                                    onClick={() => setFiltroStock(c.filtro)}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="small" style={{ opacity: 0.9 }}>{c.titulo}</div>
                                            <div className="fw-bold" style={{ fontSize: '2rem', lineHeight: 1.1 }}>{c.valor}</div>
                                        </div>
                                        <div style={{ fontSize: '2rem', opacity: 0.85 }}>{c.icono}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Buscador */}
                    <div className="d-flex gap-2 flex-wrap align-items-center mb-4">
                        <div className="position-relative flex-grow-1" style={{ maxWidth: '480px' }}>
                            <FaSearch style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                            <input
                                type="text"
                                name="busqueda_insumos_modulo"
                                autoComplete="off"
                                className="form-control"
                                style={{ paddingLeft: '38px', borderRadius: '12px' }}
                                placeholder="Buscar insumo por nombre..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                            />
                        </div>
                        {filtroStock !== 'todos' && (
                            <button className="btn btn-outline-secondary" style={{ borderRadius: '12px' }} onClick={() => setFiltroStock('todos')}>
                                Quitar filtro ✕
                            </button>
                        )}
                    </div>

                    {/* Tarjetas */}
                    <div className="row g-3">
                        {filtrados.map(i => {
                            const est = estadoDe(i);
                            const stock = parseFloat(i.stock_almacen);
                            const dias = diasDe(i.id);
                            return (
                                <div key={i.id} className="col-12 col-md-6 col-xl-4">
                                    <div
                                        className="bg-white shadow-sm h-100 d-flex flex-column"
                                        style={{ borderRadius: '18px', borderTop: `5px solid ${est.color}`, overflow: 'hidden' }}
                                    >
                                        <div className="p-3 pb-1 d-flex align-items-start justify-content-between gap-2">
                                            <div className="d-flex align-items-center gap-3">
                                                <div style={{ width: '46px', height: '46px', borderRadius: '14px', background: est.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>
                                                    📦
                                                </div>
                                                <div>
                                                    <div className="fw-bold" style={{ fontSize: '1.05rem', lineHeight: 1.2 }}>{i.nombre}</div>
                                                    <span className="badge" style={{ background: est.bg, color: est.color, borderRadius: '20px', marginTop: '4px' }}>
                                                        {est.texto}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-1">
                                                <button className="btn btn-sm btn-light" title="Editar" onClick={() => abrirEditar(i)}><FaEdit /></button>
                                                <button className="btn btn-sm btn-light text-danger" title="Eliminar" onClick={() => eliminarInsumo(i)}><FaTrash /></button>
                                            </div>
                                        </div>

                                        <div className="px-3 py-2 text-center">
                                            <div className="fw-bold" style={{ fontSize: '2.6rem', color: est.color, lineHeight: 1 }}>{fmtNum(i.stock_almacen)}</div>
                                            <div className="text-muted small">{i.unidad_medida || 'unidades'} en almacén</div>
                                        </div>

                                        <div className="px-3 pb-2 d-flex justify-content-between small text-muted">
                                            <span>Último costo: <strong>{i.ultimo_costo ? `Bs. ${parseFloat(i.ultimo_costo).toFixed(2)}` : '-'}</strong></span>
                                            {stock > 0 && (
                                                <span>{dias !== null ? `Alcanza ~${dias} día${dias === 1 ? '' : 's'}` : 'Sin entregas recientes'}</span>
                                            )}
                                        </div>

                                        <div className="p-3 pt-2 mt-auto">
                                            <button
                                                className="btn btn-primary fw-bold w-100 mb-2"
                                                style={{ borderRadius: '12px' }}
                                                disabled={stock <= 0}
                                                onClick={() => entregarDirecto(i)}
                                            >
                                                🚚 Entregar a local
                                            </button>
                                            <div className="d-flex gap-2">
                                                <button className="btn btn-outline-secondary flex-fill" style={{ borderRadius: '12px' }} onClick={() => verKardex(i)}>
                                                    <FaHistory className="me-1" /> Historial
                                                </button>
                                                <button className="btn btn-outline-primary flex-fill" style={{ borderRadius: '12px' }} onClick={() => abrirAjuste(i)}>
                                                    ± Ajustar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {!loading && filtrados.length === 0 && (
                        <div className="text-center py-5">
                            <div style={{ fontSize: '3.5rem' }}>📦</div>
                            <div className="fw-bold mt-2">{insumos.length === 0 ? 'Aún no hay insumos' : 'Ningún insumo coincide'}</div>
                            <div className="text-muted mb-3">
                                {insumos.length === 0 ? 'Crea el primero para empezar a controlar tu almacén.' : 'Prueba con otra búsqueda o quita el filtro.'}
                            </div>
                            {insumos.length === 0 && (
                                <button className="btn btn-primary fw-bold" onClick={abrirNuevo}>
                                    <FaPlus className="me-2" /> Nuevo Insumo
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {tab === 'entregas' && (
                <div className="p-4">
                    {/* Resumen */}
                    <div className="row g-3 mb-4">
                        {[
                            { titulo: 'Entregas', valor: entregas.length, icono: '🚚', grad: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)' },
                            { titulo: 'Activas', valor: entActivas.length, icono: '✅', grad: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' },
                            { titulo: 'Anuladas', valor: entAnuladas, icono: '🚫', grad: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' },
                            { titulo: 'Insumos entregados', valor: insumosDistintos, icono: '📦', grad: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }
                        ].map(c => (
                            <div key={c.titulo} className="col-6 col-lg-3">
                                <div className="text-white p-3 shadow-sm" style={{ background: c.grad, borderRadius: '16px' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="small" style={{ opacity: 0.9 }}>{c.titulo}</div>
                                            <div className="fw-bold" style={{ fontSize: '2rem', lineHeight: 1.1 }}>{c.valor}</div>
                                        </div>
                                        <div style={{ fontSize: '2rem', opacity: 0.85 }}>{c.icono}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Filtros */}
                    <div className="bg-white shadow-sm p-3 mb-4" style={{ borderRadius: '16px' }}>
                        <div className="d-flex gap-2 flex-wrap align-items-center mb-3">
                            <span className="small fw-bold text-muted me-1">Período:</span>
                            {[
                                { t: 'Hoy', d: hoyISO(), h: hoyISO() },
                                { t: 'Últimos 7 días', d: haceDiasISO(6), h: hoyISO() },
                                { t: 'Este mes', d: inicioMesISO(), h: hoyISO() },
                                { t: 'Todas', d: '', h: '' }
                            ].map(p => {
                                const activo = filtroDesde === p.d && filtroHasta === p.h;
                                return (
                                    <button
                                        key={p.t}
                                        className={`btn btn-sm ${activo ? 'btn-primary' : 'btn-outline-primary'}`}
                                        style={{ borderRadius: '20px' }}
                                        onClick={() => { setFiltroDesde(p.d); setFiltroHasta(p.h); }}
                                    >
                                        {p.t}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="d-flex gap-3 flex-wrap align-items-end">
                            <div>
                                <label className="small fw-bold d-block">Desde</label>
                                <input type="date" className="form-control" value={filtroDesde} onChange={(ev) => setFiltroDesde(ev.target.value)} />
                            </div>
                            <div>
                                <label className="small fw-bold d-block">Hasta</label>
                                <input type="date" className="form-control" value={filtroHasta} onChange={(ev) => setFiltroHasta(ev.target.value)} />
                            </div>
                            <div>
                                <label className="small fw-bold d-block">Estado</label>
                                <div className="d-flex gap-2">
                                    {[
                                        { v: '', t: 'Todas' },
                                        { v: 'activa', t: 'Activas' },
                                        { v: 'anulada', t: 'Anuladas' }
                                    ].map(op => (
                                        <button
                                            key={op.t}
                                            className={`btn ${filtroEstado === op.v ? 'btn-dark' : 'btn-outline-secondary'}`}
                                            style={{ borderRadius: '10px' }}
                                            onClick={() => setFiltroEstado(op.v)}
                                        >
                                            {op.t}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tarjetas de entregas */}
                    <div className="row g-3">
                        {entregas.map(ent => {
                            const anulada = ent.estado === 'anulada';
                            const items = ent.items || [];
                            return (
                                <div key={ent.id} className="col-12 col-xl-6">
                                    <div
                                        className="bg-white shadow-sm h-100 d-flex flex-column"
                                        style={{ borderRadius: '18px', borderLeft: `6px solid ${anulada ? '#ef4444' : '#10b981'}`, overflow: 'hidden' }}
                                    >
                                        <div className="p-3 pb-2 d-flex justify-content-between align-items-start gap-2">
                                            <div>
                                                <div className="fw-bold" style={{ fontSize: '1.2rem' }}>{ent.numero}</div>
                                                <div className="small text-muted">{fmtFecha(ent.fecha)}</div>
                                            </div>
                                            <span className="badge" style={{
                                                background: anulada ? '#fee2e2' : '#d1fae5',
                                                color: anulada ? '#dc2626' : '#059669',
                                                padding: '0.45rem 0.9rem',
                                                borderRadius: '20px'
                                            }}>
                                                {anulada ? 'ANULADA' : 'ACTIVA'}
                                            </span>
                                        </div>

                                        <div className="px-3 pb-2 d-flex align-items-center gap-2 small">
                                            <span className="badge bg-light text-dark border">👤 {ent.entregado_por || '-'}</span>
                                            <span className="text-muted">→</span>
                                            <span className="badge bg-light text-dark border">👤 {ent.recibido_por || '-'}</span>
                                        </div>

                                        <div className="px-3 pb-2 d-flex flex-wrap gap-2">
                                            {items.map(it => (
                                                <span key={it.insumo_id} className="badge" style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: '10px', padding: '0.45rem 0.7rem', fontSize: '0.85rem' }}>
                                                    {fmtNum(it.cantidad)} {it.unidad_medida || ''} · {it.nombre}
                                                </span>
                                            ))}
                                            {items.length === 0 && <span className="text-muted small">Sin ítems</span>}
                                        </div>

                                        {ent.observaciones && (
                                            <div className="px-3 pb-2 small text-muted">📝 {ent.observaciones}</div>
                                        )}
                                        {anulada && (
                                            <div className="mx-3 mb-2 p-2 small" style={{ background: '#fef2f2', borderRadius: '10px', color: '#b91c1c' }}>
                                                Motivo de anulación: {ent.anulada_motivo || '-'}
                                            </div>
                                        )}

                                        <div className="p-3 pt-2 mt-auto d-flex gap-2 flex-wrap">
                                            <button className="btn btn-primary fw-bold flex-fill" style={{ borderRadius: '12px' }} onClick={() => abrirPDF(ent.id)}>
                                                <FaPrint className="me-2" /> Imprimir comprobante
                                            </button>
                                            {!anulada && (
                                                <>
                                                    <button className="btn btn-outline-warning" style={{ borderRadius: '12px' }} onClick={() => abrirEditarEntrega(ent)}>
                                                        <FaEdit className="me-1" /> Editar
                                                    </button>
                                                    <button className="btn btn-outline-danger" style={{ borderRadius: '12px' }} onClick={() => anularEntrega(ent)}>
                                                        <FaTrash className="me-1" /> Anular
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {entregas.length === 0 && (
                        <div className="text-center py-5">
                            <div style={{ fontSize: '3.5rem' }}>🚚</div>
                            <div className="fw-bold mt-2">No hay entregas en este período</div>
                            <div className="text-muted mb-3">Cambia el período o el estado, o registra una nueva entrega.</div>
                            <button className="btn btn-primary fw-bold" onClick={abrirNuevaEntrega}>
                                <FaPlus className="me-2" /> Nueva Entrega
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* MODAL ENTREGA A LOCAL */}
            {showEntrega && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                            <div className="modal-header text-white" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)' }}>
                                <h5 className="m-0 fw-bold">
                                    {entregaEditando ? `Editar entrega ${entregaEditando.numero}` : 'Nueva entrega a local'}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEntrega(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="row g-2 mb-3">
                                    <div className="col-md-6">
                                        <label className="fw-bold small">Entregado por</label>
                                        <input
                                            type="text"
                                            name="entrega_entregado_por"
                                            autoComplete="off"
                                            className="form-control"
                                            value={entregaForm.entregado_por}
                                            onChange={(ev) => setEntregaForm({ ...entregaForm, entregado_por: ev.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="fw-bold small">Recibido por</label>
                                        <input
                                            type="text"
                                            name="entrega_recibido_por"
                                            autoComplete="off"
                                            className="form-control"
                                            placeholder="Nombre de quien recibe"
                                            value={entregaForm.recibido_por}
                                            onChange={(ev) => setEntregaForm({ ...entregaForm, recibido_por: ev.target.value })}
                                        />
                                    </div>
                                </div>

                                <label className="fw-bold small">Agregar insumo</label>
                                <div className="position-relative mb-2">
                                    <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                    <input
                                        type="text"
                                        name="busqueda_insumo_entrega"
                                        autoComplete="off"
                                        className="form-control"
                                        style={{ paddingLeft: '35px' }}
                                        placeholder="Escribe el nombre del insumo..."
                                        value={busquedaEntrega}
                                        onChange={(ev) => setBusquedaEntrega(ev.target.value)}
                                    />
                                </div>
                                {busquedaEntrega.trim() !== '' && (() => {
                                    const coincidencias = insumos
                                        .filter(i => i.nombre.toLowerCase().includes(busquedaEntrega.trim().toLowerCase()))
                                        .filter(i => !itemsEntrega.find(it => it.insumo_id === i.id))
                                        .slice(0, 8);
                                    return (
                                        <div className="border rounded mb-3" style={{ maxHeight: '190px', overflowY: 'auto' }}>
                                            {coincidencias.length === 0 && (
                                                <div className="px-3 py-2 text-muted small">Ningún insumo coincide</div>
                                            )}
                                            {coincidencias.map(i => (
                                                <div
                                                    key={i.id}
                                                    className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => agregarItemEntrega(i)}
                                                >
                                                    <span>
                                                        {i.nombre}{' '}
                                                        <small className={parseFloat(i.stock_almacen) <= 0 ? 'text-danger' : 'text-muted'}>
                                                            (hay {fmtNum(i.stock_almacen)} {i.unidad_medida || ''})
                                                        </small>
                                                    </span>
                                                    <FaPlus className="text-primary" />
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}

                                {itemsEntrega.length > 0 && (
                                    <div className="mb-3">
                                        <label className="fw-bold small d-block mb-2">📦 Insumos a entregar ({itemsEntrega.length}):</label>
                                        {itemsEntrega.map(it => {
                                            const c = parseFloat(it.cantidad);
                                            const excede = !isNaN(c) && c > it.disponible;
                                            return (
                                                <div
                                                    key={it.insumo_id}
                                                    className="d-flex align-items-center gap-3 mb-2 p-3 rounded shadow-sm"
                                                    style={{ background: '#f8fafc', border: excede ? '1px solid #ef4444' : '1px solid #e2e8f0' }}
                                                >
                                                    <div className="flex-grow-1">
                                                        <div className="fw-bold">{it.nombre}</div>
                                                        <div className={`small ${excede ? 'text-danger fw-bold' : 'text-muted'}`}>
                                                            Disponible: {fmtNum(it.disponible)} {it.unidad_medida || ''}
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        className="form-control fw-bold text-center"
                                                        style={{ maxWidth: '110px', borderRadius: '10px' }}
                                                        placeholder="Cant."
                                                        value={it.cantidad}
                                                        onChange={(ev) => cambiarCantidadItem(it.insumo_id, ev.target.value)}
                                                        onFocus={(ev) => ev.target.select()}
                                                    />
                                                    <button className="btn btn-outline-danger btn-sm" style={{ borderRadius: '10px' }} onClick={() => quitarItemEntrega(it.insumo_id)}>
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <label className="fw-bold small">Observaciones</label>
                                <textarea
                                    className="form-control mb-3"
                                    rows="2"
                                    value={entregaForm.observaciones}
                                    onChange={(ev) => setEntregaForm({ ...entregaForm, observaciones: ev.target.value })}
                                />

                                {entregaEditando && (
                                    <>
                                        <label className="fw-bold small text-danger">PIN de seguridad *</label>
                                        <input
                                            type="password"
                                            name="pin_editar_entrega"
                                            autoComplete="new-password"
                                            className="form-control form-control-lg"
                                            placeholder="Ingrese PIN"
                                            value={entregaForm.pin}
                                            onChange={(ev) => setEntregaForm({ ...entregaForm, pin: ev.target.value })}
                                        />
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowEntrega(false)}>Cancelar</button>
                                <button className="btn btn-primary fw-bold" onClick={guardarEntrega} disabled={guardandoEntrega}>
                                    <FaCheckCircle className="me-2" /> {guardandoEntrega ? 'Guardando...' : (entregaEditando ? 'Guardar cambios' : 'Registrar entrega')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB_CONTENT_EXTRA */}

            {/* MODAL CREAR / EDITAR */}
            {showForm && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                            <form onSubmit={guardarInsumo}>
                                <div className="modal-header text-white" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)' }}>
                                    <h5 className="m-0 fw-bold">{editando ? 'Editar Insumo' : 'Nuevo Insumo'}</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowForm(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <label className="fw-bold small">Nombre del insumo *</label>
                                    <input
                                        type="text"
                                        name="insumo_nombre_form"
                                        autoComplete="off"
                                        className="form-control mb-3"
                                        placeholder="Ej: Bolsas 30x40"
                                        value={form.nombre}
                                        onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                        autoFocus
                                    />
                                    <label className="fw-bold small">Unidad de medida</label>
                                    <input
                                        type="text"
                                        name="insumo_unidad_form"
                                        autoComplete="off"
                                        className="form-control mb-3"
                                        placeholder="Ej: paquete, kg, litro, unidad"
                                        value={form.unidad_medida}
                                        onChange={(e) => setForm({ ...form, unidad_medida: e.target.value })}
                                    />
                                    {!editando && (
                                        <>
                                            <label className="fw-bold small">Stock inicial en almacén</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                className="form-control"
                                                placeholder="0"
                                                value={form.stock_inicial}
                                                onChange={(e) => setForm({ ...form, stock_inicial: e.target.value })}
                                            />
                                            <small className="text-muted">Queda registrado en el kardex como stock inicial.</small>
                                        </>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                                    <button type="submit" className="btn btn-primary fw-bold">
                                        <FaCheckCircle className="me-2" /> Guardar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL AJUSTE DE STOCK */}
            {ajusteInsumo && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                            <div className="modal-header text-white" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                                <h5 className="m-0 fw-bold">Ajustar stock: {ajusteInsumo.nombre}</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setAjusteInsumo(null)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="alert alert-light border mb-3">
                                    Stock actual en almacén: <strong>{fmtNum(ajusteInsumo.stock_almacen)} {ajusteInsumo.unidad_medida || ''}</strong>
                                </div>
                                <label className="fw-bold small">Nueva cantidad *</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="form-control form-control-lg mb-2"
                                    value={ajuste.nueva_cantidad}
                                    onChange={(e) => setAjuste({ ...ajuste, nueva_cantidad: e.target.value })}
                                    onFocus={(e) => e.target.select()}
                                />
                                {diffAjuste !== null && diffAjuste !== 0 && (
                                    <div className={`small fw-bold mb-3 ${diffAjuste > 0 ? 'text-success' : 'text-danger'}`}>
                                        {fmtNum(ajusteInsumo.stock_almacen)} → {fmtNum(nuevaNum)} ({diffAjuste > 0 ? '+' : ''}{diffAjuste})
                                    </div>
                                )}
                                <label className="fw-bold small mt-2">Motivo del ajuste *</label>
                                <textarea
                                    className="form-control mb-3"
                                    rows="2"
                                    placeholder="Ej: Conteo físico, corrección de error..."
                                    value={ajuste.motivo}
                                    onChange={(e) => setAjuste({ ...ajuste, motivo: e.target.value })}
                                />
                                <label className="fw-bold small text-danger">PIN de seguridad *</label>
                                <input
                                    type="password"
                                    name="pin_ajuste_insumos"
                                    autoComplete="new-password"
                                    className="form-control form-control-lg"
                                    placeholder="Ingrese PIN"
                                    value={ajuste.pin}
                                    onChange={(e) => setAjuste({ ...ajuste, pin: e.target.value })}
                                />
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setAjusteInsumo(null)}>Cancelar</button>
                                <button className="btn btn-primary fw-bold" onClick={guardarAjuste}>
                                    <FaCheckCircle className="me-2" /> Guardar ajuste
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL KARDEX */}
            {kardexInsumo && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setKardexInsumo(null)}>
                    <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                            <div className="d-flex align-items-center justify-content-between text-white p-4" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                                <div>
                                    <h5 className="m-0 fw-bold"><FaHistory className="me-2" /> Kardex</h5>
                                    <small style={{ opacity: 0.85 }}>{kardexInsumo.nombre}</small>
                                </div>
                                <button className="btn btn-light btn-sm" style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0 }} onClick={() => setKardexInsumo(null)}>
                                    <FaTimes />
                                </button>
                            </div>
                            <div className="modal-body p-4">
                                {movimientos.length === 0 ? (
                                    <div className="text-center text-muted py-5">No hay movimientos registrados</div>
                                ) : (
                                    <table className="table table-hover align-middle mb-0">
                                        <thead style={{ background: '#f8fafc' }}>
                                            <tr>
                                                <th>FECHA</th>
                                                <th>MOVIMIENTO</th>
                                                <th>DETALLE</th>
                                                <th className="text-center">CANTIDAD</th>
                                                <th>USUARIO</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {movimientos.map(m => {
                                                const est = ESTILO_TIPO[m.tipo] || { bg: '#e2e8f0', color: '#334155', label: String(m.tipo).toUpperCase() };
                                                const cant = parseFloat(m.cantidad);
                                                return (
                                                    <tr key={m.id}>
                                                        <td className="small">{fmtFecha(m.fecha)}</td>
                                                        <td>
                                                            <span className="badge" style={{ background: est.bg, color: est.color, padding: '0.45rem 0.9rem', borderRadius: '20px' }}>
                                                                {est.label}
                                                            </span>
                                                        </td>
                                                        <td className="small text-muted">
                                                            {m.observaciones || (m.tipo === 'compra' && m.referencia_id ? `Compra (egreso #${m.referencia_id})` : '-')}
                                                        </td>
                                                        <td className="text-center fw-bold fs-5" style={{ color: cant > 0 ? '#10b981' : '#ef4444' }}>
                                                            {cant > 0 ? '+' : ''}{fmtNum(m.cantidad)}
                                                        </td>
                                                        <td>{m.usuario || 'Sistema'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            <div className="modal-footer bg-light">
                                <button className="btn btn-secondary" onClick={() => setKardexInsumo(null)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
